import React, { useEffect, useState } from 'react';
import { EvidenceScope, Matches, RecentMatch, WorkflowView, getWorkflowMatches } from '../../../api/cafAutomation';
import { formatCount, formatDayTime } from '../format';
import { useLenderRulesReady } from '../lenders';
import { Modal } from '../Overlay';
import { DIFFERENCE, decisionOutcomeLine, gateSentence, readinessIssueSentence } from './automationCopy';
import { PredictedSteps } from './PredictedSteps';
import { chipLabel } from './steps';
import { exampleLabel } from './WorkflowConfirms';
import { StepChips } from './StepChips';
import { DecisionComparison } from './DecisionComparison';
import { AutomationRunDrawer, replacementRunLabel } from './AutomationRunDrawer';

const lower = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

export const RecentMatchesModal: React.FC<{ view: WorkflowView; scope?: EvidenceScope; onClose: () => void }> = ({ view, scope = 'caf-workflows-1', onClose }) => {
  const { rules, ready } = useLenderRulesReady();
  const [data, setData] = useState<Matches | null>(null);
  const [problem, setProblem] = useState('');
  const [decisionId, setDecisionId] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    getWorkflowMatches(view.workflow_id, scope).then(
      (next) => { if (alive) setData(next); },
      () => { if (alive) setProblem("Couldn't load the recent matches. Close and try again."); },
    );
    return () => { alive = false; };
  }, [view.workflow_id, scope]);

  const would = (claimReference: string | null) => (view.steps || [])
    .map((card) => (card.kind === 'attach' ? `attach to ${claimReference || 'the claim'}` : lower(chipLabel(card))))
    .join(', ');
  const followed = (item: RecentMatch) => {
    if (item.rule_key) return 'A lender emailed about a cancelled claim.';
    const entry = view.used_by.find((candidate) => candidate.entry_id === item.followed_entry_id);
    return `The AI followed ${entry ? exampleLabel(entry, rules, ready) : 'one of its examples'}.`;
  };
  const sentence = (item: RecentMatch) => (item.gate_ok
    ? `✓ ${followed(item)} The workflow would: ${would(item.claim_reference)}.`
    : `– ${gateSentence(item.reason)} The workflow wouldn't run.`);

  return (
    <Modal title={`Recent matches: ${view.name}`} size="wide" onClose={onClose}
      footer={<button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose}>Close</button>}>
      {problem && <p className="cur-hint">{problem}</p>}
      {!data && !problem && <p className="cur-hint">Loading…</p>}
      {data && (
        <>
          {scope === 'phase1' ? <p>Historical Phase 1 coded action evidence. It is separate from replacement workflow evidence.</p>
            : <p>Each decision below shows the version and steps saved at the time. A proposed ending is not proof of completion.</p>}
          {!data.items.length && <div className="cur-empty">The AI hasn&apos;t followed one of its examples yet.</div>}
          <ol className="cur-timeline">
            {data.items.map((item, index) => (
              <li key={`${item.decided_at}-${index}`} className="cur-tl">
                <span className="cur-tl-dot" aria-hidden="true" />
                <div className="cur-tl-head">
                  <strong>{item.subject || 'No subject'}</strong>
                  <span className="cur-muted">{formatDayTime(item.decided_at)}</span>
                </div>
                <div className="cur-tl-reason">{scope === 'phase1' ? sentence(item) : <>
                  <p>{item.workflow?.name || 'Manual handling'}{item.workflow?.version ? ` · version ${item.workflow.version}` : ''} · {item.source?.label || 'No source recorded'}</p>
                  {item.plan && <><StepChips steps={item.plan.cards || []} /><p>Proposed ending: {item.plan.ending || 'None'}</p></>}
                  <PredictedSteps predictions={item.predictions} />
                  {(item.readiness_issues || []).slice(0, 1).map((issue) => <p key={issue}>{readinessIssueSentence(issue)}</p>)}
                  <p>{decisionOutcomeLine(item)}</p>
                  {item.run && <p>Run: {replacementRunLabel(item.run)}</p>}
                  {item.decision_id && <button type="button" className="cur-linkish" onClick={() => setDecisionId(item.decision_id!)}>Compare with live</button>}
                  {item.run_id && <button type="button" className="cur-linkish" onClick={() => setRunId(item.run_id!)}>Open run</button>}
                </>}</div>
              </li>
            ))}
          </ol>
          {scope === 'phase1' && data.built_in && (
            <section>
              <h3 className="cur-sec-title">Compared with the built-in action</h3>
              <p>{`Same ${formatCount(data.built_in.same)} · Different ${formatCount(data.built_in.different)}`}</p>
              {data.built_in.not_covered > 0 && (
                <p>{`The built-in action ran ${formatCount(data.built_in.not_covered)} times where this workflow wouldn't start.`}</p>
              )}
              <ul className="cur-subjects">
                {data.built_in.differences.map((item, index) => (
                  <li key={`${item.decided_at}-${index}`}>
                    {`${item.subject || 'No subject'} · ${item.claim_reference || 'no claim'} · ${DIFFERENCE[item.why] || ''}`}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {scope === 'phase1' && <p className="cur-hint">This historical comparison records no replacement workflow action.</p>}
        </>
      )}
      {decisionId && <DecisionComparison decisionId={decisionId} scope={scope} onClose={() => setDecisionId(null)} />}
      {runId && <AutomationRunDrawer runId={runId} onClose={() => setRunId(null)} />}
    </Modal>
  );
};
